import type { AudioMessageFrame } from '@/protocol/frames';

/**
 * Web Audio playback of the raw PCM segments the server streams, one per synthesized sentence.
 * Must be created from a user gesture to satisfy the browser's autoplay policy, which is why
 * {@link AudioPlayer.initialize} is called from the connect click rather than at startup.
 */
export class AudioPlayer {
  private context: AudioContext | null = null;
  private gain: GainNode | null = null;
  private nextPlayTime = 0;
  private muted = false;

  /**
   * Everything scheduled and not yet finished. Held so playback can be cut off: a source that has
   * been started plays to its end on its own, and the server pacing several sentences ahead of the
   * speaker means most of the reply is already scheduled by the time the user talks over it.
   */
  private scheduled = new Set<AudioBufferSourceNode>();

  initialize(): void {
    if (this.context) return;
    this.context = new AudioContext();
    this.gain = this.context.createGain();
    this.gain.gain.value = this.muted ? 0 : 1;
    this.gain.connect(this.context.destination);
    this.nextPlayTime = 0;
  }

  /**
   * Decodes a base64 signed-16-bit-little-endian segment and schedules it immediately after
   * whatever is already queued, so consecutive sentences play gaplessly.
   */
  play(frame: AudioMessageFrame): void {
    const ctx = this.context;
    if (!ctx || !frame.base64Audio) return;

    if (ctx.state === 'suspended') void ctx.resume();

    const bytes = base64ToBytes(frame.base64Audio);
    const sampleRate = frame.sampleRate ?? 16000;
    const channels = frame.channels ?? 1;

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const frameCount = Math.floor(bytes.byteLength / 2 / channels);
    if (frameCount === 0) return;

    const buffer = ctx.createBuffer(channels, frameCount, sampleRate);
    for (let ch = 0; ch < channels; ch++) {
      const channelData = buffer.getChannelData(ch);
      for (let i = 0; i < frameCount; i++) {
        channelData[i] = view.getInt16((i * channels + ch) * 2, true) / 32768;
      }
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.gain ?? ctx.destination);

    const startAt = Math.max(ctx.currentTime, this.nextPlayTime);
    source.onended = () => this.scheduled.delete(source);
    this.scheduled.add(source);
    source.start(startAt);
    this.nextPlayTime = startAt + buffer.duration;
  }

  /**
   * Cuts playback off now, dropping whatever is still scheduled. Done locally rather than by waiting
   * for the server to stop sending: the server paces its audio open-loop and is seconds of speech
   * ahead of the speaker, so everything already here would play out regardless of what it does next.
   */
  stop(): void {
    for (const source of this.scheduled) {
      source.onended = null;
      source.stop();
    }
    this.scheduled.clear();
    this.nextPlayTime = 0;
  }

  /** Takes effect instantly, even mid-segment. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.gain) this.gain.gain.value = muted ? 0 : 1;
  }
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
